import React, { useMemo, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, ArrowDown, BrainCircuit, Zap, Code2 } from "lucide-react";
import { Laptop, Tablet, Phone } from "./mockups/Devices";
import { WA_ORCAMENTO, scrollToId } from "../lib/site";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Chip = ({ icon: Icon, label, className = "", delay = 1 }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
    transition={{
      opacity: { delay, duration: 0.6 },
      scale: { delay, duration: 0.6 },
      y: { duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: delay + 0.4 },
    }}
    className={`absolute z-20 flex items-center gap-2 rounded-full border border-white/15 bg-[#0B0B16]/85 px-3.5 py-2 backdrop-blur-md ${className}`}
  >
    <Icon className="h-3.5 w-3.5 text-[#00E5FF]" />
    <span className="text-[11px] font-medium tracking-wide text-white/80">{label}</span>
  </motion.div>
);

const Hero = () => {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 55, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 55, damping: 16 });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const yDevices = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacityHero = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const dots = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => ({
        left: (i * 37 + 11) % 100,
        top: (i * 53 + 7) % 100,
        size: 2 + (i % 3),
        duration: 5 + (i % 5),
        delay: (i % 7) * 0.55,
      })),
    []
  );

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="inicio"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-[72px]"
    >
      <div className="bg-grid absolute inset-0 opacity-70 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_72%)]" />
      <div className="pointer-events-none absolute -left-40 top-24 h-96 w-96 rounded-full bg-[#00E5FF]/10 blur-[130px]" />
      <div className="pointer-events-none absolute -right-32 bottom-10 h-[28rem] w-[28rem] rounded-full bg-[#8A2BE2]/15 blur-[140px]" />
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {dots.map((d, i) => (
          <motion.span
            key={i}
            className="absolute rounded-full bg-[#00E5FF]"
            style={{ left: `${d.left}%`, top: `${d.top}%`, width: d.size, height: d.size }}
            animate={{ y: [0, -16, 0], opacity: [0.12, 0.55, 0.12] }}
            transition={{ duration: d.duration, repeat: Infinity, delay: d.delay, ease: "easeInOut" }}
          />
        ))}
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-16 px-6 pb-24 pt-16 lg:grid-cols-2 lg:gap-10 lg:px-10">
        <motion.div style={{ y: yText, opacity: opacityHero }}>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00E5FF] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00E5FF]" />
            </span>
            <span className="text-[11px] font-semibold tracking-[0.28em] text-white/70">
              SOLUÇÕES DIGITAIS SOB MEDIDA
            </span>
          </motion.div>

          <h1 className="mt-8 font-display text-[2.4rem] font-bold leading-[1.08] tracking-normal sm:text-6xl sm:leading-[1.05] lg:text-[4.2rem]">
            <MaskedLine delay={0.25}>TECNOLOGIA QUE</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-gradient">TRANSFORMA</span>
            </MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="text-gradient">NEGÓCIOS.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-7 max-w-xl text-base font-light leading-relaxed text-white/70 md:text-lg"
          >
            Criamos soluções digitais sob medida para empresas que querem crescer,
            automatizar processos e fortalecer sua presença digital.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <a
              href={WA_ORCAMENTO}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-orcamento"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-8 py-4 text-sm font-semibold text-white shadow-[0_14px_44px_-12px_rgba(0,229,255,0.55)] transition-[transform,filter] duration-300 hover:scale-[1.03] hover:brightness-110"
            >
              Solicitar orçamento
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => scrollToId("solucoes")}
              data-testid="hero-cta-solucoes"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-8 py-4 text-sm font-semibold text-white backdrop-blur transition-colors duration-300 hover:bg-white/10"
            >
              Conhecer soluções
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yDevices, opacity: opacityHero }} className="relative">
          <div
            style={{ perspective: 1200 }}
            onMouseMove={onMove}
            onMouseLeave={() => {
              mx.set(0);
              my.set(0);
            }}
          >
            <motion.div
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="relative mx-auto h-[400px] max-w-[560px] sm:h-[480px] lg:h-[540px]"
            >
              <div className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-[#00E5FF]/15 to-[#8A2BE2]/20 blur-[90px]" />
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
                className="absolute left-0 top-[22%] w-[72%]"
              >
                <Laptop />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.1, delay: 0.75, ease: EASE }}
                className="absolute right-0 top-0 w-[42%]"
              >
                <Tablet />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.1, delay: 0.95, ease: EASE }}
                className="absolute bottom-0 right-[8%] w-[24%]"
              >
                <Phone />
              </motion.div>
              <Chip icon={BrainCircuit} label="Inteligência Artificial" delay={1.25} className="left-0 top-0" />
              <Chip icon={Zap} label="Automação" delay={1.4} className="bottom-[30%] left-[2%]" />
              <Chip icon={Code2} label="Sob medida" delay={1.55} className="bottom-[4%] left-[38%]" />
            </motion.div>
          </div>
        </motion.div>
      </div>

      <motion.button
        type="button"
        onClick={() => scrollToId("solucoes")}
        data-testid="hero-scroll-indicator"
        aria-label="Rolar para soluções"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 lg:block"
      >
        <span className="flex h-12 w-7 items-start justify-center rounded-full border border-white/20 p-1.5">
          <motion.span
            className="h-2 w-1 rounded-full bg-[#00E5FF]"
            animate={{ y: [0, 18, 0], opacity: [1, 0.2, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.button>
    </section>
  );
};

export default Hero;
